<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-transcript-search" :class="{'no-data':hasNoData}" v-loading="loading" element-loading-text="Loading...">
    <div v-show="transcriptSearchShowHeader">
      <div class="transcript-search-header">
        <div>
          <div class="flex-title">
            <div class="wy-page-title-small">{{$t(currentPageInfo.subtitle)}}</div>
          </div>
          <p class="title">{{$t(currentPageInfo.title)}}
            <wy-page-tooltip />
          </p>
        </div>

        <div class="transcript-buttons">
          <el-button v-if="isRoleAllow(4200) && this.$refs.transcriptSearchResults && this.$refs.transcriptSearchResults.totalMatchedConversations>0 && transcriptSearchAvailable" class="submit-btn transcript-detail-export-btn" round @click="applyExport">
              <div class="export-icon"></div>
              <span class="export-text">Export</span> 
          </el-button> 
        </div> 
      </div>
      <div v-if="transcriptSearchAvailable">
        <wy-transcript-search-filters ref="transcriptSearchFilters" :controller="module" :class="isTranscriptExpanded ? 'transcript-filter-hidden': ''"/>
        <wy-transcript-search-results ref="transcriptSearchResults" :controller="module" @handle-transcript-click="handleTranscriptModal" from="Transcript Search"/>
      </div>
      <no-data-page v-else> </no-data-page>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
import WyTranscriptSearchFilters from "./wy-interactions-transcript-search-filters.vue";
import WyTranscriptSearchResults from "./wy-interactions-transcript-search-results.vue";
import WyPageTooltipVue from "../../widgets/wy-page-tooltip.vue";
import noDataPage from '../../../views/PageNoDataWithAvailableDateRange';

export default Vue.extend({
  name: "wy-analytics-transcript-search",
  components: {
    'wy-transcript-search-filters': WyTranscriptSearchFilters,
    'wy-transcript-search-results': WyTranscriptSearchResults,
    'wy-page-tooltip': WyPageTooltipVue,
    'no-data-page': noDataPage
  },

  data() {
    return {
      module: this,
      loading: false,
      transcriptSearchAvailable: true,
      transcriptSearchShowHeader : false,
      isTranscriptExpanded: false,
      initDate: null,
      hasNoData: false
    };
  },

  computed: {
    ...mapGetters(["dataModel", "isRoleAllow", "currentPageInfo", "isLoggedIn",  "transcriptInfoReference"]),
  },

  methods: {
    ...mapActions(["fetchTranscriptSearchTimeRange"]),
    ...mapMutations(['updateTranscriptInfoReference']), //handle transcript info

    handleTranscriptPopover() {
      if (this.transcriptInfoReference) {
        this.transcriptInfoReference.visible = false;
        this.updateTranscriptInfoReference(null);
      }
    },

    async handleFilterChange(eventType) {
      this.$root.$emit(`fire-global-filter-change-events`, true);
      if ((!this.loading && this.dataModel.dateEnd) || this.dataModel.dateEnd) {
        this.loading = true;
        this.transcriptSearchAvailable = true;
        this.transcriptSearchShowHeader = false;        
        let timeRangeResult = await this.fetchTranscriptSearchTimeRange();

        // do the search when timeRangeResult's range time is not -1 && must have contact reasons
        if(timeRangeResult && timeRangeResult.data 
        && timeRangeResult.data.minHandlingTime > -1 && timeRangeResult.data.maxHandlingTime > -1
        && timeRangeResult.data.minTransferCount > -1 && timeRangeResult.data.maxTransferCount > -1 
        && timeRangeResult.data.minAgentHandlingTime > -1 && timeRangeResult.data.maxAgentHandlingTime > -1
        && timeRangeResult.data.minAgentResponseTime > -1 && timeRangeResult.data.maxAgentResponseTime > -1 
        && timeRangeResult.data.minAgentWaitTime > -1 && timeRangeResult.data.maxAgentWaitTime > -1 
        && this.$refs.transcriptSearchResults) 
        {
          this.$refs.transcriptSearchResults.prepareTimeRange(timeRangeResult);
          this.$refs.transcriptSearchResults.loadTopicConversations(1, false, eventType);
        }
        else {
          this.transcriptSearchShowHeader = true;
          this.transcriptSearchAvailable = false;
          this.loading = false;
        }
      }
    },
    applyExport() {
      if(this.$refs.transcriptSearchResults && this.$refs.transcriptSearchResults.applyExport) {
        this.$refs.transcriptSearchResults.applyExport()
      }
    },
    handleTranscriptModal(value) {
      this.isTranscriptExpanded = value;
    }
  },
  async created() {
    this.initDate = new Date()
    // listen events from other components
    this.$root.registerPageEvent(this.$route.name, (event) => {
      if (this[event.name]) this[event.name](event.type);
    });

    //let today = new Date();
    await this.handleFilterChange();
  },
  mounted() {
    // on resize window, we close the transcript popover
    window.addEventListener('resize', this.handleTranscriptPopover);

    // on scroll, we close the transcript popover & any opened dropdowns
    document.querySelector('.wy-transcriptSearch-page .wy-container-main').addEventListener('scroll', () => {
      this.handleTranscriptPopover();
    });

    // Outside of metric-analysis
    document.addEventListener('click', () => {
      this.handleTranscriptPopover();
    });

    // Inside metric-analysis
    document.querySelector('.wy-analytics-transcript-search').addEventListener('click', () => {
      this.handleTranscriptPopover();
    });
  }
});
</script>

<style lang="scss">
.wy-transcriptSearch-page{
  .wy-container-main, .wy-page-modal {
    background-color: #FFF !important;
  }
  .wy-container-main {
    padding-right: 0px !important;
  }
}
.wy-analytics-transcript-search.no-data {
  min-height: 710px !important;
}
.wy-analytics-transcript-search {
  // min-width: 1123px;
  // max-width: 1130px;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 1315px;
  position: relative;
  min-height: calc(100% - 20px);
  height: 100%;
  padding-right: 0px;
  margin-bottom: 0px;
  color: var(--site-font-tertiary-color);
  padding-right: 20px !important;

  .transcript-search-header{
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: -10px;

    .transcript-buttons {
      margin-left: auto;
      .submit-btn.transcript-detail-export-btn {
        // background: transparent;
        box-sizing: border-box;
        border-radius: 5px;
        font-weight: 500 !important;
        // border: 1px solid #CAD2DA !important;
        background: var(--site-button-secondary-btn-background-primary-color);
        color: var(--site-button-secondary-btn-text-primary-color);
        border: 1px solid var(--site-button-secondary-btn-hover-border-primary-color);
        padding: 6px 15px 9px 11px !important;
        width: 90px;
        font-size: 16px !important;
        height: 36px;
        // color: var(--site-font-tertiary-color) !important;
        float: right;
        // &:hover {
        //   background-color: #F4F6F8;
        // }
      }
      .submit-btn.transcript-detail-export-btn:hover {
        border: 1px var(--site-button-secondary-btn-hover-border-primary-color) solid !important;
        background-color: var(--site-button-secondary-btn-hover-background-primary-color) !important;
        border-radius: 5px;
        color: var(--site-button-secondary-btn-text-primary-color) !important;
      }
      .submit-btn.transcript-detail-export-btn:active {
        background-color: var(--site-button-secondary-btn-pressed-background-primary-color) !important;
      }
      .submit-btn.transcript-detail-export-btn:focus {
        border: 1px var(--site-button-secondary-btn-hover-border-primary-color) solid !important;
        border-radius: 5px;
        color: var(--site-button-secondary-btn-text-primary-color) !important;
      }
      .export-arrow{
        position: relative;
        top: 2px;
        right: -1px;
      }
      .export-text{
        margin-top: 2px;
        position: relative;
        bottom: 1px;
        left: 5px;
      }
      .export-icon {
        background: var(--icon_action_export);
        height: 17px;
        width: 13px;
        position: relative;
        top: 2px;
        height: 17px;
        width: 13px;
        display: inline-block;
        background-repeat: no-repeat;
      }
    }
  }
  .transcript-search-info-icon {
    width: 20px;
    margin-bottom: -3px;
  }
  .title {
    color: var(--site-font-secondary-color);
    font-size: 25px;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-weight: 600;
    margin: 0px 0px 6px 0px;
    margin-bottom: 35px;
    line-height: 0.7;
    height: 25px;
  }

  .flex-title {
    display: flex;
    align-items: center;
    position: relative;
    right: 35px;
  }

  .wy-page-title {
    margin-bottom: 0px;
  }

  .wy-page-title-small {
    display: none;
    color: var(--site-font-secondary-color);
    font-size: 15px;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-weight: 400;
    padding-right: 35px;
    padding-left: 35px;
  }
}
.search-title-tooltips {
  width: 400px;
}
.transcript-filter-hidden {
  display: none;
}
@media (max-width: 1830px) {
  // .wy-interactions-transcript-search-results .search-result-content .div-transcripts-viewer-content {
  //   height: calc(100vh - 377px);
  // }
}

// from media query min width 2184 px, 
@media (min-width: 2173px) {
  .wy-interactions-transcript-search-results .search-result-content .div-transcripts-viewer-content {
    height: calc(100vh - 290px);
  }
}
</style>./wy-interactions-transcript-search-filters.vue/index.js./wy-interactions-transcript-search-results.vue/index.js